import React, { useContext, useEffect, useState } from 'react';

import { SocietyProviderDataForm, SocietyProviderFilters } from '../../app/models/SocietyProvider';
import useSocietyProviderService from '../../app/services/hooks/useSocietyProviderService';
import { toast } from 'react-toastify';
import ProviderCreate from './components/ProviderCreate';
import ProviderDataTable from './components/ProviderDataTable';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import { AppContext } from '../../contexts/AppContext';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import DefaultModal from '../../components/default/DefaultModal';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Proveedores',
        url: '/providers',
        isActive: true
    }
];

const Providers = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { 
        fetchingGetSocietyProviders, 
        getSocietyProviders, 
        deleteSocietyProvider, 
        activeSocietyProvider 
    } = useSocietyProviderService();
    const [societyProviders, setSocietyProviders] = useState<SocietyProviderDataForm[]>([]);

    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const { navigationPage } = useNavigationPage();

    const [filter, setFilter] = useState<SocietyProviderFilters>({
        society_id: undefined,
        rut: '',
        business_name: '',
        name: '',
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order, filter.society_id]);

    const reloadTable = () => {
        getSocietyProviders(filter, {
            onSuccess: (response: ServiceResponse) => {
                setSocietyProviders(response.data.society_providers || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (societyProviderId: number) => {
        navigationPage('/providers/' + societyProviderId + '/edit');
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const destroy = (societyProviderId: number) => {
        const _text = 'Está a punto de eliminar el proveedor de la sociedad #' + societyProviderId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando proveedor...');
                        deleteSocietyProvider(societyProviderId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El proveedor no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (societyProviderId: number, active: boolean) => {
        activeSocietyProvider(societyProviderId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Proveedores" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo proveedor" />}
                />

                <ProviderDataTable
                    providers={societyProviders.map(sp => ({
                        id: sp.id,
                        society: sp.society,
                        rut: sp.provider?.rut || '',
                        business_name: typeof sp.provider?.business_name === 'string'
                            ? sp.provider.business_name
                            : sp.provider?.business_name?.name || '',
                        address: sp.provider?.address || '',
                        country: sp.provider?.country || '',
                        is_active: sp.is_active ?? true
                    }))}
                    totalRows={totalRows}
                    loading={fetchingGetSocietyProviders}
                    destroy={destroy}
                    edit={showEdit}
                    active={active}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear proveedor"
                    backdrop={true}
                    showFooter={false}
                >
                    <ProviderCreate
                        onSaved={(societyId?: number) => {
                            hideCreate();
                            // Si se creó un proveedor, actualizar el filtro con la sociedad si es necesario
                            if (societyId && !filter.society_id) {
                                setFilter({
                                    ...filter,
                                    society_id: societyId,
                                    page: 1
                                });
                            } else {
                                reloadTable();
                            }
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Providers;
